import './style.css';
import React, { useEffect, useState } from 'react';
import { useToast, Modal } from '@economic/gaudi';
import LoadingBox from '../../pages/home/PlanUpgradeDialog/LoadingBox';
import { t, useLocalization } from '../../shared/components/l10n/l10n';
import useCachedQuery from '../../shared/hooks/useCachedQuery';
import StepAccountingYear from './StepAccountingYear';
import StepBankInfo from './StepBankInfo';
import StepBusinessInfo from './StepBusinessInfo';
import StepCompanyType from './StepCompanyType';
import StepSuccess from './StepSuccess';
import StepWelcome from './StepWelcome';
import StepEconomicGo from './StepEconomicGo';
import { OnboardingStatus } from './types';
import { useCurrentAgreement } from '@economic/react-app';
import react from 'react';

interface Props {
    onDone: () => void;
}

function GettingStartedDialog({ onDone }: Props) {
    const [step, setStep] = useState(0);
    const [show, setShow] = useState(true);
    const [hasAccountant, setHasAccountant] = react.useState<boolean | undefined>(undefined);

    const { loading, data, refetch } = useCachedQuery<OnboardingStatus>('onboarding');
    const loadingTexts = useLocalization(textIds);
    const toast = useToast();

    useEffect(() => {
        if (step > 0) return;

        // Account activation information is shown in the dialog body rather than in toast.
        var messageShownInDialog = !loading && data && data.accountActivationLinkSent;

        if (!loadingTexts && !loading && !messageShownInDialog) {
            toast.success(t`TOAST_GETTING_STARTED_COMPLETE_SUCCESS`);
        }
    }, [loadingTexts, loading]);

    if (loadingTexts) {
        return null;
    }

    function back() {
        if (step > 1) setStep(step - 1);
    }

    async function reloadAndNext() {
        await refetch();
        next();
    }
    function next() {
        setStep(step + 1);
    }

    function close() {
        setShow(false);
        window.top?.refreshRouting();
        window.location.pathname = '/';
        onDone();
    }

    return (
        <Modal visible={show} showCloseButton={false} hideOnEsc={false}>
            {loading && !data ? (
                <LoadingBox />
            ) : (
                <>
                    {step === 0 && (
                        <StepWelcome displaySubcontent={data.accountActivationLinkSent} onGettingStarted={() => setStep(1)} />
                    )}
                    {step === 1 && (
                        <StepBusinessInfo
                            companyData={data.companyData}
                            onDone={reloadAndNext}
                            hasAccountant={hasAccountant}
                            setHasAccountant={value => {
                                setHasAccountant(value);
                            }}
                        />
                    )}
                    {step === 2 && (
                        <StepCompanyType
                            setups={data.setups}
                            onBack={back}
                            onDone={reloadAndNext}
                            onSkip={next}
                            stepCount={hasAccountant === false ? 5 : 4}
                        />
                    )}
                    {step === 3 && (
                        <StepAccountingYear
                            data={data.accountingYear}
                            onBack={back}
                            onDone={reloadAndNext}
                            stepCount={hasAccountant === false ? 5 : 4}
                        />
                    )}
                    {step === 4 && (
                        <StepBankInfo
                            company={data.companyData}
                            onBack={back}
                            onDone={reloadAndNext}
                            onSkip={next}
                            stepCount={hasAccountant === false ? 5 : 4}
                        />
                    )}
                    {step === 5 && hasAccountant !== false && <StepSuccess onDone={close} />}
                    {step === 5 && hasAccountant === false && <StepEconomicGo onSkip={next} />}
                    {step === 6 && <StepSuccess onDone={close} />}
                </>
            )}
        </Modal>
    );
}

function withGettingStarted(Component: React.ComponentType<{ onDone: () => void }>) {
    return (props: any) => {
        const agreement = useCurrentAgreement();

        if (!agreement.showGettingStarted) {
            return null;
        }

        return <Component {...props} />;
    };
}

export default withGettingStarted(GettingStartedDialog);

const textIds = [
    'ONBOARDING_GETTINGSTARTED_WELCOME_TITLE',
    'ONBOARDING_GETTINGSTARTED_WELCOME_CONTENT',
    'ONBOARDING_GETTINGSTARTED_WELCOME_SUBCONTENT',
    'ONBOARDING_GETTINGSTARTED_WELCOME_CTA',
    'ONBOARDING_GETTINGSTARTED_TOAST_TITLE',
    'TOAST_GETTING_STARTED_COMPLETE_SUCCESS',

    'ONBOARDING_GETTINGSTARTED_BUSINESSINFO_TITLE',
    'ONBOARDING_GETTINGSTARTED_BUSINESSINFO_CONTENT',
    'ONBOARDING_GETTINGSTARTED_BUSINESSINFO_CVR',
    'ONBOARDING_GETTINGSTARTED_BUSINESSINFO_CONTACTPERSON',
    'ONBOARDING_GETTINGSTARTED_BUSINESSINFO_NAME',
    'ONBOARDING_GETTINGSTARTED_BUSINESSINFO_EMAIL',
    'ONBOARDING_GETTINGSTARTED_BUSINESSINFO_ZIPCODE',
    'ONBOARDING_GETTINGSTARTED_BUSINESSINFO_CITY',
    'ONBOARDING_GETTINGSTARTED_BUSINESSINFO_PHONE',
    'ONBOARDING_GETTINGSTARTED_BUSINESSINFO_ADDRESS',
    'ONBOARDING_GETTINGSTARTED_BUSINESSINFO_WEBSITE',
    'ONBOARDING_GETTINGSTARTED_BUSINESSINFO_VALIDATION_REQUIRED_MESSAGE',
    'ONBOARDING_GETTINGSTARTED_BUSINESSINFO_EMAIL_VALIDATION_MESSAGE',
    'ONBOARDING_GETTINGSTARTED_BUSINESSINFO_PHONE_VALIDATION_MESSAGE',

    'ONBOARDING_GETTINGSTARTED_COMPANYTYPE_TITLE',
    'ONBOARDING_GETTINGSTARTED_COMPANYTYPE_CONTENT',
    'ONBOARDING_GETTINGSTARTED_COMPANYTYPE_MUSTCHOOSESETUP',
    'ONBOARDING_GETTINGSTARTED_COMPANYTYPE_SEEMORE',

    'ONBOARDING_GETTINGSTARTED_ACCOUNTINGYEAR_TITLE',
    'ONBOARDING_GETTINGSTARTED_ACCOUNTINGYEAR_CONTENT',

    'ONBOARDING_GETTINGSTARTED_BANKINFO_TITLE',
    'ONBOARDING_GETTINGSTARTED_BANKINFO_CONTENT',
    'ONBOARDING_GETTINGSTARTED_BANKINFO_NAME',
    'ONBOARDING_GETTINGSTARTED_BANKINFO_ACCOUNT',
    'ONBOARDING_GETTINGSTARTED_BANKINFO_NUMBER',
    'ONBOARDING_GETTINGSTARTED_BANKINFO_SWIFT',
    'ONBOARDING_GETTINGSTARTED_BANKINFO_IBAN',
    'ONBOARDING_GETTINGSTARTED_BANKINFO_NAME_REQUIRED',

    'ONBOARDING_GETTINGSTARTED_SUCCESS_TITLE',
    'ONBOARDING_GETTINGSTARTED_SUCCESS_CONTENT',
    'ONBOARDING_GETTINGSTARTED_SUCCESS_CTA',
    'ONBOARDING_GETTINGSTARTED_SUCCESS_TIP1',
    'ONBOARDING_GETTINGSTARTED_SUCCESS_TIP2',
    'ONBOARDING_GETTINGSTARTED_SUCCESS_TIP3',

    'ONBOARDING_GETTINGSTARTED_BACK',
    'ONBOARDING_GETTINGSTARTED_NEXT',
    'ONBOARDING_GETTINGSTARTED_SKIP',

    // Old, from gettingStarted.aspx and reused here
    'GETTING_STARTED_ACCOUNTING_YEAR_OPTION_CURRENT',
    'GETTING_STARTED_ACCOUNTING_YEAR_OPTION_SHIFTED',
    'GETTING_STARTED_ACCOUNTING_YEAR_OPTION_CUSTOM',
    'GETTING_STARTED_ACCOUNTYEARS_ALREADY_CREATED',
    'GETTING_STARTED_ACCOUNTYEARS_ALREADY_CREATED_HINT_CHANGE_I',
    'GETTING_STARTED_ACCOUNTYEARS_ALREADY_CREATED_HINT_CHANGE_II_PART_I',
    'GETTING_STARTED_ACCOUNTYEARS_ALREADY_CREATED_HINT_CHANGE_II_PART_II',
    'GETTING_STARTED_ACCOUNTYEARS_ALREADY_CREATED_HINT_CHANGE_II_PART_III',
    'GETTING_STARTED_DEFAULT_SETUP_NAME_ALREADY_APPLIED',
    'GETTING_STARTED_DEFAULT_SETUP_NAME_ALREADY_APPLIED_HINT_CHANGE_I',
    'GETTING_STARTED_DEFAULT_SETUP_NAME_ALREADY_APPLIED_HINT_CHANGE_II_PART_I',
    'GETTING_STARTED_DEFAULT_SETUP_NAME_ALREADY_APPLIED_HINT_CHANGE_II_PART_II',
    'GETTING_STARTED_DEFAULT_SETUP_NAME_ALREADY_APPLIED_HINT_CHANGE_II_PART_III',
    'GETTING_STARTED_CANNOT_APPLY_DEFAULT_SETUP_REASON',
    'GETTING_STARTED_DEFAULT_SETUP_CANNOT_APPLY_MORE_INFO',
    //economic go
    'ECONOMIC_GO_YES',
    'ECONOMIC_GO_NO',
];
